import { useState, useEffect, useMemo } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Loader2, RefreshCw } from "lucide-react";

interface SnapTradeUser {
  snaptrade_user_id: string;
  x_user_id: string;
}

interface SnapTradeDataTabProps {
  endpoint: "positions" | "orders";
  columns: { key: string; label: string; align?: "left" | "right"; render?: (row: any) => React.ReactNode }[];
  emptyLabel: string;
}

export function SnapTradeDataTab({ endpoint, columns, emptyLabel }: SnapTradeDataTabProps) {
  const [users, setUsers] = useState<SnapTradeUser[]>([]);
  const [selectedUser, setSelectedUser] = useState("");
  const [accounts, setAccounts] = useState<any[]>([]);
  const [selectedAccount, setSelectedAccount] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingAccounts, setLoadingAccounts] = useState(false);
  const [data, setData] = useState<any[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      const { data } = await supabase
        .from("snaptrade_users")
        .select("snaptrade_user_id, x_user_id")
        .not("snaptrade_user_id", "is", null);
      setUsers(data || []);
      if (data && data.length > 0) setSelectedUser(data[0].snaptrade_user_id);
    })();
  }, []);

  // Auto-fetch accounts when user changes
  useEffect(() => {
    if (!selectedUser) return;
    setAccounts([]);
    setSelectedAccount("");
    setData(null);
    setLoadingAccounts(true);
    callSnaptrade("accounts", selectedUser).then((result) => {
      if (Array.isArray(result)) {
        setAccounts(result);
        if (result.length > 0) setSelectedAccount(result[0].id);
      }
      setLoadingAccounts(false);
    });
  }, [selectedUser]);

  async function callSnaptrade(ep: string, userId: string, accountId?: string) {
    const { data: { session } } = await supabase.auth.getSession();
    const projectId = import.meta.env.VITE_SUPABASE_PROJECT_ID;
    const resp = await fetch(
      `https://${projectId}.supabase.co/functions/v1/snaptrade-test`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session?.access_token}`,
        },
        body: JSON.stringify({
          endpoint: ep,
          snaptrade_user_id: userId,
          account_id: accountId || undefined,
        }),
      }
    );
    const json = await resp.json();
    if (!resp.ok) throw new Error(json.error || `HTTP ${resp.status}`);
    return json.data;
  }

  async function fetchData() {
    if (!selectedUser || !selectedAccount) return;
    setLoading(true);
    setError(null);
    setData(null);
    try {
      const result = await callSnaptrade(endpoint, selectedUser, selectedAccount);
      setData(Array.isArray(result) ? result : [result]);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }

  // Auto-fetch data when account is selected
  useEffect(() => {
    if (selectedAccount) fetchData();
  }, [selectedAccount]);

  return (
    <div className="space-y-4">
      {/* Controls */}
      <div className="flex flex-wrap gap-3 items-end">
        <div className="space-y-1.5">
          <label className="text-xs text-muted-foreground font-medium">User</label>
          <Select value={selectedUser} onValueChange={setSelectedUser}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Select user" />
            </SelectTrigger>
            <SelectContent>
              {users.map((u) => (
                <SelectItem key={u.snaptrade_user_id} value={u.snaptrade_user_id}>
                  {u.x_user_id}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-1.5">
          <label className="text-xs text-muted-foreground font-medium">Account</label>
          {loadingAccounts ? (
            <div className="flex h-10 items-center px-3 text-sm text-muted-foreground">
              <Loader2 className="h-3.5 w-3.5 animate-spin mr-2" /> Loading…
            </div>
          ) : (
            <Select value={selectedAccount} onValueChange={setSelectedAccount}>
              <SelectTrigger className="w-[220px]">
                <SelectValue placeholder="Select account" />
              </SelectTrigger>
              <SelectContent>
                {accounts.map((a: any) => (
                  <SelectItem key={a.id} value={a.id}>
                    {a.name || a.id} ({a.number || "—"})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>

        <Button variant="outline" size="sm" onClick={fetchData} disabled={loading || !selectedAccount} className="gap-1.5">
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </Button>
      </div>

      {/* Error */}
      {error && (
        <div className="rounded-md bg-destructive/10 border border-destructive/30 p-3 text-sm text-destructive">
          {error}
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 text-primary animate-spin" />
        </div>
      )}

      {/* Data table */}
      {!loading && data !== null && (
        data.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <p className="text-sm text-muted-foreground">{emptyLabel}</p>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="text-sm">
                  <thead>
                    <tr className="border-b border-border text-muted-foreground text-xs">
                      {columns.map((col) => (
                        <th
                          key={col.key}
                          className={`px-4 py-3 font-medium ${col.align === "right" ? "text-right" : "text-left"}`}
                        >
                          {col.label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {data.map((row: any, i: number) => (
                      <tr key={i} className="border-b border-border/50 hover:bg-muted/30 transition-colors">
                        {columns.map((col) => (
                          <td
                            key={col.key}
                            className={`px-4 py-3 ${col.align === "right" ? "text-right font-mono" : ""}`}
                          >
                            {col.render ? col.render(row) : getNestedValue(row, col.key) ?? "—"}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )
      )}
    </div>
  );
}

function getNestedValue(obj: any, path: string): any {
  return path.split(".").reduce((o, k) => o?.[k], obj);
}

/* ─── Pre-configured Tabs ─── */

export function SnapTradePositionsTab() {
  return (
    <SnapTradeDataTab
      endpoint="positions"
      emptyLabel="No open positions found for this account."
      columns={[
        { key: "symbol.symbol.symbol", label: "Symbol", render: (r) => (
          <span className="font-mono font-medium">
            {r.symbol?.symbol?.symbol || r.symbol?.symbol || "—"}
          </span>
        )},
        { key: "units", label: "Units", align: "right", render: (r) => r.units ?? r.quantity ?? "—" },
        { key: "price", label: "Price", align: "right", render: (r) => {
          const p = r.price ?? r.average_purchase_price;
          return p != null ? `$${Number(p).toFixed(2)}` : "—";
        }},
        { key: "open_pnl", label: "Open P&L", align: "right", render: (r) => {
          const pnl = r.open_pnl;
          if (pnl == null) return "—";
          const val = Number(pnl);
          return (
            <span className={val >= 0 ? "text-profit" : "text-loss"}>
              {val >= 0 ? "+" : ""}{val.toFixed(2)}
            </span>
          );
        }},
        { key: "average_purchase_price", label: "Avg Cost", align: "right", render: (r) => {
          const v = r.average_purchase_price;
          return v != null ? `$${Number(v).toFixed(2)}` : "—";
        }},
      ]}
    />
  );
}

export function SnapTradeOrdersTab() {
  return (
    <SnapTradeDataTab
      endpoint="orders"
      emptyLabel="No orders found for this account."
      columns={[
        { key: "symbol", label: "Symbol", render: (r) => (
          <span className="font-mono font-medium">
            {r.universal_symbol?.symbol || r.symbol || "—"}
          </span>
        )},
        { key: "action", label: "Action", render: (r) => {
          const action = r.action || r.side || "—";
          const isBuy = action.toLowerCase().includes("buy");
          return (
            <Badge variant="secondary" className={isBuy ? "bg-profit/10 text-profit" : "bg-loss/10 text-loss"}>
              {action}
            </Badge>
          );
        }},
        { key: "filled_quantity", label: "Qty", align: "right", render: (r) => r.filled_quantity ?? r.total_quantity ?? "—" },
        { key: "execution_price", label: "Price", align: "right", render: (r) => {
          const p = r.execution_price ?? r.limit_price;
          return p != null ? `$${Number(p).toFixed(2)}` : "—";
        }},
        { key: "status", label: "Status", render: (r) => (
          <Badge variant="outline" className="text-xs">{r.status || "—"}</Badge>
        )},
        { key: "time_placed", label: "Date", align: "right", render: (r) => {
          const d = r.time_placed || r.time_executed;
          if (!d) return "—";
          try { return new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }); }
          catch { return "—"; }
        }},
      ]}
    />
  );
}
